/**
 * CurrencyFormatter - Classe utilitária centralizada para manipulação de valores monetários
 * 
 * PADRÃO DO SISTEMA:
 * - Backend SEMPRE retorna valores em CENTAVOS (integer)
 * - Frontend SEMPRE exibe valores em REAIS formatados (string)
 * - Esta classe garante conversão consistente em todo o sistema
 * 
 * @version 1.0.0
 * @author Pagmus Team
 */

class CurrencyFormatter {
  
  /**
   * Converte centavos para reais formatados
   * @param {number|string} cents - Valor em centavos (ex: 80000)
   * @returns {string} - Valor formatado (ex: "R$ 800,00")
   */
  static centsToFormatted(cents) {
    if (!cents || isNaN(cents)) return 'R$ 0,00';
    
    const reais = parseFloat(cents) / 100;
    return reais.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  /**
   * Converte centavos para número decimal em reais
   * @param {number|string} cents - Valor em centavos (ex: 80000)
   * @returns {number} - Valor em reais (ex: 800.00)
   */
  static centsToReais(cents) {
    if (!cents || isNaN(cents)) return 0;
    return parseFloat(cents) / 100;
  }

  /**
   * Converte reais para centavos
   * @param {number|string} reais - Valor em reais (ex: 800.00)
   * @returns {number} - Valor em centavos (ex: 80000)
   */
  static reaisToCents(reais) {
    if (!reais || isNaN(reais)) return 0;
    return Math.round(parseFloat(reais) * 100);
  }

  /**
   * Formata input de usuário para máscara R$ 0,00
   * @param {string} value - Valor digitado pelo usuário
   * @returns {string} - Valor com máscara aplicada
   */
  static formatInput(value) {
    // Remove tudo que não seja número
    const numbers = value.replace(/\D/g, '');
    
    // Se não há números, retorna vazio
    if (!numbers) return '';
    
    // Converte para número e formata
    const amount = parseFloat(numbers) / 100;
    
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /**
   * Converte valor formatado pelo usuário para centavos
   * @param {string} formattedValue - Valor digitado com máscara (ex: "800,00")
   * @returns {number} - Valor em centavos (ex: 80000)
   */
  static parseInputToCents(formattedValue) {
    if (!formattedValue) return 0;
    
    // Remove formatação e converte para centavos
    const numbers = formattedValue.replace(/\D/g, '');
    return parseInt(numbers) || 0;
  }

  /**
   * Detecta e normaliza valores que podem vir em formatos inconsistentes
   * @param {any} value - Valor em formato desconhecido
   * @returns {number} - Valor normalizado em centavos
   */
  static normalize(value) {
    if (!value) return 0;

    // Se é string formatada (ex: "R$ 800,00")
    if (typeof value === 'string' && value.includes('R$')) {
      const cleanValue = value.replace(/[R$\s.]/g, '').replace(',', '.');
      return Math.round(parseFloat(cleanValue) * 100);
    }

    // Se é string numérica (ex: "800.00", "80000")
    if (typeof value === 'string') {
      const numValue = parseFloat(value);
      // Se tem casas decimais, provavelmente é em reais
      if (value.includes('.') && numValue < 10000) {
        return Math.round(numValue * 100);
      }
      // Caso contrário, assume que já está em centavos
      return Math.round(numValue);
    }

    // Se é número
    if (typeof value === 'number') {
      // Se é menor que 10000, provavelmente é em reais
      if (value < 10000 && value % 1 !== 0) {
        return Math.round(value * 100);
      }
      // Caso contrário, assume que já está em centavos
      return Math.round(value);
    }

    return 0;
  }

  /**
   * Formata faixa de preços
   * @param {number} minCents - Valor mínimo em centavos
   * @param {number} maxCents - Valor máximo em centavos
   * @returns {string} - Faixa formatada ou valor único
   */
  static formatRange(minCents, maxCents) {
    if (!minCents && !maxCents) return 'R$ 0,00';
    
    const min = this.centsToFormatted(minCents);
    const max = this.centsToFormatted(maxCents);
    
    if (minCents === maxCents) {
      return min;
    }
    
    return `De ${min} até ${max}`;
  }

  /**
   * Calcula porcentagem de um valor
   * @param {number} cents - Valor base em centavos
   * @param {number} percentage - Porcentagem (ex: 15 para 15%)
   * @returns {number} - Resultado em centavos
   */
  static calculatePercentage(cents, percentage) {
    if (!cents || !percentage) return 0;
    return Math.round((cents * percentage) / 100);
  }

  /**
   * Soma valores em centavos
   * @param {...number} values - Valores em centavos
   * @returns {number} - Soma em centavos
   */
  static sum(...values) {
    return values.reduce((total, value) => total + (this.normalize(value) || 0), 0);
  }

  /**
   * Valida se um valor monetário é válido
   * @param {any} value - Valor a ser validado
   * @returns {boolean} - True se válido
   */
  static isValid(value) {
    const normalized = this.normalize(value);
    return normalized > 0;
  }

  /**
   * Formata valor para exibição em tabelas/listas (sem símbolo de moeda)
   * @param {number} cents - Valor em centavos
   * @returns {string} - Valor formatado sem R$ (ex: "800,00")
   */
  static toDisplayValue(cents) {
    if (!cents || isNaN(cents)) return '0,00';
    
    const reais = parseFloat(cents) / 100;
    return reais.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /**
   * Formata valor compacto para dashboards (ex: "800", "1,2K", "1,5M")
   * @param {number} cents - Valor em centavos
   * @returns {string} - Valor compacto
   */
  static toCompactValue(cents) {
    if (!cents || isNaN(cents)) return 'R$ 0';
    
    const reais = parseFloat(cents) / 100;
    
    if (reais >= 1000000) {
      return `R$ ${(reais / 1000000).toFixed(1)}M`;
    }
    
    if (reais >= 1000) {
      return `R$ ${(reais / 1000).toFixed(1)}K`;
    }
    
    return `R$ ${reais.toFixed(0)}`;
  }
}

export default CurrencyFormatter;























